import { useState, useMemo, useRef, useEffect } from 'react'
import { Avatar, Chip, Button, ChatContainer, ChatBubble, Alert } from '@/components/ui'
import { Search, Send, Phone, UserCircle2, Bot, CheckCircle2, Loader2, RefreshCcw } from 'lucide-react'
import { cn, fmtMoney } from '@/lib/utils'
import {
  useCwConversations, useCwMessages, useCwSendMessage, useCwToggleStatus,
  type CwConversation, type CwMessage,
} from '@/lib/api/chatwoot'

function fmtTime(ts: number): string {
  const d = new Date(ts * 1000)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) return d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
  const yest = new Date(now); yest.setDate(now.getDate() - 1)
  if (d.toDateString() === yest.toDateString()) return 'Ayer'
  return d.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit' })
}

function phoneFromConv(c: CwConversation): string {
  return c.meta?.sender?.phone_number ?? ''
}

export function Bandeja() {
  const [tabStatus, setTabStatus] = useState<'open' | 'resolved' | 'pending'>('open')
  const { data: conversations = [], isLoading, isFetching, refetch } = useCwConversations(tabStatus)
  const [selId, setSelId] = useState<number | null>(null)
  const [filtro, setFiltro] = useState('')
  const [draft, setDraft] = useState('')
  const [envioError, setEnvioError] = useState<string | null>(null)
  const chatScrollRef = useRef<HTMLDivElement>(null)

  const sendMsg = useCwSendMessage()
  const toggle = useCwToggleStatus()

  useEffect(() => {
    if (!selId && conversations.length > 0) setSelId(conversations[0].id)
  }, [conversations, selId])

  const sel = useMemo(() => conversations.find(c => c.id === selId) ?? conversations[0] ?? null, [conversations, selId])
  const { data: messages = [], isLoading: loadingMsgs } = useCwMessages(sel?.id ?? null)
  const telefono = sel ? phoneFromConv(sel) : ''

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight
  }, [messages.length])

  const filtradas = useMemo(() => {
    const q = filtro.trim().toLowerCase()
    if (!q) return conversations
    return conversations.filter(c => {
      const name = c.meta?.sender?.name?.toLowerCase() ?? ''
      const phone = (c.meta?.sender?.phone_number ?? '').toLowerCase()
      return name.includes(q) || phone.includes(q)
    })
  }, [conversations, filtro])

  const handleEnviar = async () => {
    if (!sel || !draft.trim()) return
    setEnvioError(null)
    try {
      await sendMsg.mutateAsync({ conversation_id: sel.id, content: draft.trim() })
      setDraft('')
    } catch (e) {
      setEnvioError((e as Error).message)
    }
  }

  const handleResolver = async () => {
    if (!sel) return
    await toggle.mutateAsync({ conversation_id: sel.id, status: 'resolved' })
    setSelId(null)
  }

  return (
    <div className="space-y-3">
      <Alert variant="info" title="Bandeja Chatwoot embebida (API nativa)">
        Conectado directo a la API de Chatwoot — sin iframe. Las conversaciones, mensajes y envío son <strong>reales</strong>. El agente responde desde acá y el cliente lo recibe en WhatsApp vía Flujo 12.
      </Alert>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden h-[720px] grid grid-cols-[320px_1fr_320px]">
        {/* COL 1 — LISTA */}
        <div className="border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b border-slate-200 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={filtro}
                onChange={e => setFiltro(e.target.value)}
                placeholder="Buscar por nombre o número…"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 bg-slate-50"
              />
            </div>
            <div className="flex gap-1 text-[11px]">
              {(['open', 'pending', 'resolved'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => { setTabStatus(s); setSelId(null) }}
                  className={cn(
                    'px-2 py-1 rounded font-semibold transition',
                    tabStatus === s ? 'bg-navy-700 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  )}
                >
                  {s === 'open' ? 'Abiertas' : s === 'pending' ? 'Pendientes' : 'Resueltas'}
                </button>
              ))}
              <button
                onClick={() => refetch()}
                className="ml-auto px-2 py-1 rounded bg-slate-100 text-slate-500 hover:bg-slate-200"
                title="Refrescar"
                disabled={isFetching}
              >
                {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
              </button>
            </div>
          </div>
          <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {isLoading && (
              <li className="p-4 text-center text-slate-500 text-xs"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Cargando…</li>
            )}
            {!isLoading && filtradas.length === 0 && (
              <li className="p-4 text-center text-slate-500 text-xs">
                {conversations.length === 0 ? `Sin conversaciones ${tabStatus}` : 'Sin coincidencias con el filtro'}
              </li>
            )}
            {filtradas.map(c => {
              const name = c.meta?.sender?.name ?? phoneFromConv(c) ?? `#${c.id}`
              const lastMsg = c.messages?.[0]
              return (
                <li
                  key={c.id}
                  onClick={() => setSelId(c.id)}
                  className={cn(
                    'px-3 py-2.5 flex items-start gap-2 cursor-pointer transition',
                    sel?.id === c.id ? 'bg-emerald-50 border-l-4 border-emerald-500' : 'hover:bg-slate-50 border-l-4 border-transparent'
                  )}
                >
                  <Avatar name={name} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-navy-700 text-sm truncate">{name}</span>
                      {lastMsg && <span className="text-[10px] text-slate-500 shrink-0">{fmtTime(lastMsg.created_at)}</span>}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-xs text-slate-500 truncate flex-1">
                        {lastMsg?.content ?? (c.meta?.sender?.phone_number ?? `conv #${c.id}`)}
                      </span>
                      {(c.unread_count ?? 0) > 0 && (
                        <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>

        {/* COL 2 — CHAT */}
        <div className="flex flex-col">
          {!sel && (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              Elegí una conversación para ver el chat
            </div>
          )}
          {sel && (
            <>
              <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={sel.meta?.sender?.name ?? telefono} size="md" />
                  <div className="min-w-0">
                    <div className="font-semibold text-navy-700 truncate">{sel.meta?.sender?.name ?? telefono ?? `conv #${sel.id}`}</div>
                    <div className="text-xs text-slate-500 font-mono">{telefono || `chatwoot #${sel.id}`}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Chip variant="emerald"><Bot className="w-3 h-3 mr-1 inline" /> Copilot</Chip>
                  {sel.status !== 'resolved' && (
                    <Button variant="emerald" size="sm" onClick={handleResolver} disabled={toggle.isPending}>
                      {toggle.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      Resolver
                    </Button>
                  )}
                </div>
              </header>

              <div ref={chatScrollRef} className="flex-1 overflow-y-auto">
                <ChatContainer className="min-h-full">
                  {loadingMsgs && <div className="text-center text-slate-500 text-xs py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Cargando mensajes…</div>}
                  {messages.map((m: CwMessage) => {
                    const isIn = m.message_type === 0
                    const tag = m.sender?.type === 'AgentBot' || m.sender?.type === 'User'
                      ? <Chip variant={m.sender?.type === 'AgentBot' ? 'emerald' : 'blue'} className="text-[9px]">
                          {m.sender?.type === 'AgentBot' ? <><Bot className="w-2.5 h-2.5 mr-0.5 inline" /> bot</> : (m.sender?.name ?? 'agente')}
                        </Chip>
                      : undefined
                    return (
                      <ChatBubble key={m.id} from={isIn ? 'in' : 'out'} time={fmtTime(m.created_at)} tag={tag}>
                        {m.content || <span className="italic text-slate-400">[sin texto]</span>}
                      </ChatBubble>
                    )
                  })}
                  {!loadingMsgs && messages.length === 0 && (
                    <div className="text-center text-slate-400 text-xs py-10">Sin mensajes en esta conversación</div>
                  )}
                </ChatContainer>
              </div>

              <div className="border-t border-slate-200 p-3 bg-white">
                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault()
                        handleEnviar()
                      }
                    }}
                    placeholder="Escribir como humano… (Ctrl+Enter envía)"
                    rows={2}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 resize-none"
                  />
                  <Button variant="amber" size="sm" disabled={!draft.trim() || sendMsg.isPending} onClick={handleEnviar}>
                    {sendMsg.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Enviar
                  </Button>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  {envioError ? <span className="text-rose-600">{envioError}</span> : <>Al enviar, el copilot pausa automáticamente. El mensaje sale por WhatsApp.</>}
                </div>
              </div>
            </>
          )}
        </div>

        {/* COL 3 — PERFIL */}
        <div className="border-l border-slate-200 overflow-y-auto">
          {sel && (
            <>
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <Avatar name={sel.meta?.sender?.name ?? telefono} size="lg" />
                <div className="font-display text-xl text-navy-700 mt-2 truncate">
                  {sel.meta?.sender?.name ?? telefono}
                </div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{telefono || `chatwoot #${sel.id}`}</div>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" className="flex-1"><Phone className="w-3.5 h-3.5" /> Llamar</Button>
                  <Button variant="outline" size="sm" className="flex-1"><UserCircle2 className="w-3.5 h-3.5" /> Ficha</Button>
                </div>
              </div>
              <section className="p-4 text-xs text-slate-500 leading-relaxed">
                Perfil extendido (vincular cédula + datos Point) disponible en el dashboard productivo.
                <br />
                Conversación Chatwoot <code>#{sel.id}</code> · status <code>{sel.status}</code>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Dummy export para evitar unused-import de fmtMoney cuando el perfil extendido no se usa
export const __keep = { fmtMoney }
